import Observer from '@researchgate/react-intersection-observer';
import classNames from 'classnames';
import React, {useEffect} from 'react';

import {ClickEvent, Tracker} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {SpotIllustration} from '@udemy/illustrations';
import {ASSET_CDN_DOMAIN} from '@udemy/nextjs-core';
import {Image} from '@udemy/react-core-components';
import {Button} from '@udemy/react-core-components';
import {
    OnePaneLayoutContainer,
    OnePaneLayoutFullWidthSection,
} from '@udemy/react-layout-components';
import {Tabs} from '@udemy/react-structure-components';

import {ContentSection} from 'src/components/content-section/content-section';
import {CourseSection} from 'src/components/course-section/course-section';
import {getLayout} from 'src/components/layout';
import {PageProps} from 'src/types/page-props';

import {
    AWS_COURSES,
    AZURE_COURSES,
    GOOGLE_COURSES,
    NATIONAL_COURSES,
    OTHER_QUALIFICATIONS,
    VENDOR_COURSES,
} from '../constants';
import styles from './home-page.module.less';

const env = process.env.DEPLOY_ENV;
const assetPrefixMap = {
    local: `https://${ASSET_CDN_DOMAIN.dev}/frontends-jp-campaign-itcert`,
    dev: `https://${ASSET_CDN_DOMAIN.dev}/frontends-jp-campaign-itcert`,
    prod: `https://${ASSET_CDN_DOMAIN.prod}/frontends-jp-campaign-itcert`,
};

const Marquee = () => {
    const isMobile = useMatchMedia('mobile-max');
    if (isMobile) {
        return (
            <div className={classNames(styles['marquee'], styles['marquee-mobile'])}>
                <div>
                    <Image alt="banner" src={`${assetPrefixMap[env]}/banner-white.png`} />
                </div>
                <div className={classNames(styles['inner-container-mobile'])}>
                    <h1 className={classNames('ud-heading-xxl')}>
                        効率良く学べるおすすめIT資格対策講座
                    </h1>
                    <div className="ud-text-medium">
                        初心者向け講座から模擬試験問題集など、自分に合った講座を見つけよう！
                    </div>
                </div>
            </div>
        );
    } else {
        return (
            <div className={classNames(styles['marquee'], styles['marquee-md'])}>
                <div className={classNames(styles['inner-container'])}>
                    <h1 className={classNames('ud-heading-serif-xxl')}>
                        効率良く学べるおすすめIT資格対策講座
                    </h1>
                    <div className="ud-text-large">初心者向け講座から模擬試験問題集など、</div>
                    <div className="ud-text-large">自分に合った講座を見つけよう！</div>
                </div>
                <div>
                    <Image alt="banner" src={`${assetPrefixMap[env]}/banner-white.png`} />
                </div>
            </div>
        );
    }
};

const StickyTab = () => {
    const isMobile = useMatchMedia('mobile-max');
    const [activeTab, setActiveTab] = React.useState<string | unknown>('national');
    const [floatingHeaderIsVisible, setFloatingHeaderIsVisible] = React.useState(false);

    const onSelectTab = (id: string | unknown) => {
        setActiveTab(id);
        Tracker.publishEvent(
            new ClickEvent({
                componentName: id as string,
            }),
        );
        const element = document.getElementById(id as string);
        if (element) {
            const yOffset = -50;
            const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
            window.scrollTo({top: y, behavior: 'smooth'});
        }
    };

    function handleHeaderIntersection(entry: IntersectionObserverEntry) {
        const intersectionRatio = entry.intersectionRatio;

        // If the header fully exits view, show the floating header.
        if (!floatingHeaderIsVisible && intersectionRatio === 0) {
            setFloatingHeaderIsVisible(true);
        }
    }

    function handleTopMarkIntersection(entry: IntersectionObserverEntry) {
        const {top} = entry.boundingClientRect;

        // However, only restore the non-floating header if we scroll back up to the top of the page.
        if (floatingHeaderIsVisible && top >= 0) {
            setFloatingHeaderIsVisible(false);
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const sectionIds = [
        'national',
        'microsoft',
        'aws',
        'vendor',
        'google_cloud',
        'other_qualification',
        'browse',
    ];

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveTab(entry.target.id);
                    }
                });
            },
            {
                root: null,
                rootMargin: '-50px 0px -50% 0px', // Adjust margin to detect when section is in focus
                threshold: 0.3, // Trigger when 30% of the section is visible
            },
        );

        // Observe each section
        sectionIds.forEach((id) => {
            const section = document.getElementById(id);
            if (section) observer.observe(section);
        });

        return () => {
            observer.disconnect();
        };
    }, [sectionIds]);

    return (
        <>
            <Observer onChange={handleTopMarkIntersection}>
                <span className={styles.mark} />
            </Observer>
            <Observer onChange={handleHeaderIntersection}>
                <div
                    className={classNames(
                        {
                            ['desktop-tabs']: !isMobile,
                            [styles['sticky-tabs']]: floatingHeaderIsVisible,
                        },
                        styles['sticky-tabs-desktop'],
                    )}
                >
                    <Tabs
                        prioritizeTouch
                        size="large"
                        activeTabId={activeTab}
                        onSelect={onSelectTab}
                    >
                        <Tabs.Tab id="national" title="国家資格・国家試験"></Tabs.Tab>
                        <Tabs.Tab id="aws" title="AWS認定試験"></Tabs.Tab>
                        <Tabs.Tab id="microsoft" title="Microsoft Azure認定試験"></Tabs.Tab>
                        <Tabs.Tab id="other_qualification" title="その他資格試験"></Tabs.Tab>
                        <Tabs.Tab
                            id="vendor"
                            title="ベンダー資格・ベンダーニュートラル資格"
                        ></Tabs.Tab>
                        <Tabs.Tab id="google_cloud" title="Google Cloud認定試験"></Tabs.Tab>
                        <Tabs.Tab id="browse" title="もっと講座を見る"></Tabs.Tab>
                    </Tabs>
                </div>
            </Observer>
        </>
    );
};

export const Steps = () => {
    return (
        <OnePaneLayoutContainer>
            <div className={classNames(styles['title-section'], styles['top-margin-md'])}>
                <h1 className={classNames('ud-heading-serif-xxl')}>受講するまでの3ステップ</h1>
            </div>
            <div
                className={classNames(
                    styles['steps-container'],
                    styles['top-margin-xl'],
                    styles['bottom-margin-xl'],
                )}
            >
                <div className={classNames(styles['step'])}>
                    <div className={classNames(styles['step-image'])}>
                        <SpotIllustration alt="" name="specially-curated" />
                    </div>
                    <div className={classNames(styles['step-title'])}>ステップ1.講座を探す</div>
                    <div className={classNames(styles['step-description'])}>
                        213,000以上のコースから、視聴したい講座を探しましょう。カテゴリやおすすめトピック、キーワードで講座を探せます。
                    </div>
                </div>
                <div className={classNames(styles['step'])}>
                    <div className={classNames(styles['step-image'])}>
                        <SpotIllustration alt="" name="course-management" />
                    </div>
                    <div className={classNames(styles['step-title'])}>ステップ2.講座を選ぶ</div>
                    <div className={classNames(styles['step-description'])}>
                        プレビュー動画や他の受講生のレビューを参考に、あなたにぴったりの講座を選びましょう。
                    </div>
                </div>
                <div className={classNames(styles['step'])}>
                    <div className={classNames(styles['step-image'])}>
                        <SpotIllustration alt="" name="introduction" />
                    </div>
                    <div className={classNames(styles['step-title'])}>ステップ3.購入する</div>
                    <div className={classNames(styles['step-description'])}>
                        視聴したい講座が決まったら、「今すぐ購入する」ボタンから購入可能！30日間返金保証付き。
                    </div>
                </div>
            </div>
        </OnePaneLayoutContainer>
    );
};

export function HomePage(_props: PageProps) {
    const isMobile = useMatchMedia('mobile-max');
    return (
        <OnePaneLayoutContainer>
            <OnePaneLayoutFullWidthSection>
                <div className={classNames({[styles['page-container']]: !isMobile})}>
                    <Marquee />
                </div>
                <StickyTab />
                <div
                    className={classNames(
                        styles['top-margin-xl'],
                        styles['bottom-margin-xl'],
                        styles['description'],
                    )}
                >
                    IT資格には、国家資格からベンダー資格まで難易度が異なるものがたくさんあります。
                    Udemyには、本番レベルに近い模擬試験問題集の提供がある講座や、初心者向けに資格取得に必要な知識を体系的に解説している講座など、数多くの資格対策講座があります。
                    今回は、IT資格に特化しておすすめの資格対策講座を厳選しました。業務に役立つ資格やキャリアアップに必要な資格のために、ぜひ自分に合った講座を見つけてください！
                </div>
                <div className={classNames({[styles['page-container']]: !isMobile})}>
                    <CourseSection
                        page={'it-cert'}
                        category="national"
                        title={'国家資格・国家試験'}
                        description={
                            'ITパスポート・基本情報技術者試験・応用情報技術者試験・情報セキュリティマネジメント試験・データベーススペシャリスト試験・情報処理安全確保支援士試験'
                        }
                        courses={NATIONAL_COURSES}
                        actionButton={
                            <Button
                                udStyle="secondary"
                                size="large"
                                componentClass={'a'}
                                href="https://www.udemy.com/topic/information-technology-engineers-exam/"
                                target="_blank"
                                onClick={() => {
                                    Tracker.publishEvent(
                                        new ClickEvent({
                                            componentName: 'ITEngineersExaminationCourses',
                                        }),
                                    );
                                }}
                            >
                                すべてのITエンジニア向け資格・試験講座を探す
                            </Button>
                        }
                    />
                    <CourseSection
                        page={'it-cert'}
                        category="aws"
                        title={'AWS認定試験'}
                        description={
                            'AWS（Amazon Web Services）はAmazonの子会社が提供するクラウドサービスです。'
                        }
                        courses={AWS_COURSES}
                        actionButton={
                            <Button
                                udStyle="secondary"
                                size="large"
                                componentClass={'a'}
                                href="https://www.udemy.com/browse/certification/aws-certifications/"
                                target="_blank"
                                onClick={() => {
                                    Tracker.publishEvent(
                                        new ClickEvent({
                                            componentName: 'AWSCertificationCourses',
                                        }),
                                    );
                                }}
                            >
                                すべてのAWS認定試験講座を見る
                            </Button>
                        }
                    />
                    <CourseSection
                        page={'it-cert'}
                        category="microsoft"
                        title={'Microsoft Azure認定試験'}
                        description={
                            'Microsoft Azureは、マイクロソフトが提供するクラウドコンピューティングサービスです。'
                        }
                        courses={AZURE_COURSES}
                        actionButton={
                            <Button
                                udStyle="secondary"
                                size="large"
                                componentClass={'a'}
                                href="https://www.udemy.com/browse/certification/microsoft-certifications/"
                                target="_blank"
                                onClick={() => {
                                    Tracker.publishEvent(
                                        new ClickEvent({
                                            componentName: 'MicrosoftCertificationCourses',
                                        }),
                                    );
                                }}
                            >
                                すべてのMicrosoft Azure認定資格講座を見る
                            </Button>
                        }
                    />
                    <CourseSection
                        page={'it-cert'}
                        category="other_qualification"
                        title={'その他資格試験'}
                        description={'G検定・E資格・統計検定・生成AIパスポート'}
                        courses={OTHER_QUALIFICATIONS}
                        actionButton={
                            <Button
                                id={'browse'}
                                udStyle="secondary"
                                size="large"
                                componentClass={'a'}
                                href="https://www.udemy.com/"
                                target="_blank"
                                onClick={() => {
                                    Tracker.publishEvent(
                                        new ClickEvent({
                                            componentName: 'findCoursesButton',
                                        }),
                                    );
                                }}
                            >
                                Udemyで講座を探す
                            </Button>
                        }
                    />
                    <div
                        className={classNames(styles['top-margin-xl'], styles['bottom-margin-xl'])}
                    >
                        <ContentSection
                            title="IT資格をUdemyで学習するメリット"
                            content={
                                <div
                                    className={classNames(
                                        styles['description'],
                                        styles['top-margin-md'],
                                        styles['bottom-margin-xl'],
                                    )}
                                >
                                    <ul>
                                        <li>
                                            認定資格、検定試験によっては、模擬の問題集が数多く用意されています
                                        </li>
                                        <li>
                                            模擬試験をシステムで受けることができ、採点も自動で行われます
                                        </li>
                                        <li>
                                            問題に丁寧な解説を付けている講座が多く、効率的に見直すことができます
                                        </li>
                                        <li>
                                            基礎知識に関して体系的に解説している講座のなかには、解説の合間に小テストを配置しており、理解度を確認しながら学習を進めることができます
                                        </li>
                                    </ul>
                                    <div className={classNames(styles['top-margin-xl'])}>
                                        書籍とは違い、模擬試験をUdemyのシステム上で回答することができ、自分で採点する必要もないため、効率良く資格対策をすることができます。
                                    </div>
                                    <div>
                                        また、隙間時間にPC・スマートフォンなど好きなデバイスからのアクセスが可能で必要な時に必要なだけ学習を進められます。
                                    </div>
                                </div>
                            }
                        />
                    </div>
                    <CourseSection
                        page={'it-cert'}
                        category="vendor"
                        title={'ベンダー資格・ベンダーニュートラル資格'}
                        description={
                            'CCNA・ORACLE MASTER・Java SE・LinuC・LPIC・CompTIA・PMP®認定資格・Google アナリティクス認定資格・Tableau Desktop Specialist・MOS Excel・Excel VBA'
                        }
                        courses={VENDOR_COURSES}
                        actionButton={
                            <Button
                                udStyle="secondary"
                                size="large"
                                componentClass={'a'}
                                href="https://www.udemy.com/courses/it-and-software/it-certification/"
                                target="_blank"
                                onClick={() => {
                                    Tracker.publishEvent(
                                        new ClickEvent({
                                            componentName: 'ITQualificationCourses',
                                        }),
                                    );
                                }}
                            >
                                すべてのIT資格講座を見る
                            </Button>
                        }
                    />
                    <CourseSection
                        page={'it-cert'}
                        category="google_cloud"
                        title={'Google Cloud認定試験'}
                        description={
                            'GCPとは、Google Cloud Platform の略で、Googleが提供するクラウドコンピューティングサービスの総称です。'
                        }
                        courses={GOOGLE_COURSES}
                        actionButton={
                            <Button
                                udStyle="secondary"
                                size="large"
                                componentClass={'a'}
                                href="https://www.udemy.com/browse/certification/google-cloud-certifications/"
                                target="_blank"
                                onClick={() => {
                                    Tracker.publishEvent(
                                        new ClickEvent({
                                            componentName: 'GoogleCloudCertificationCourses',
                                        }),
                                    );
                                }}
                            >
                                すべてのGoogle Cloud認定試験講座を見る
                            </Button>
                        }
                    />
                    <Steps />
                </div>
            </OnePaneLayoutFullWidthSection>
        </OnePaneLayoutContainer>
    );
}

HomePage.getLayout = getLayout;
